import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldAlert, Info } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FeatheredScrollArea } from '@/components/ui/feathered-scroll-area'
import { formatDateTime } from '@/lib/dateUtils'

/**
 * Choose, per object, between the compromised copy and the last clean one.
 *
 * The request-wide "recover red objects, yes or no?" is still the default and still does the work
 * for most recoveries. This exists because one answer cannot cover a real incident: an
 * investigator may want the COMPROMISED copy of a domain controller precisely because it is the
 * evidence, and the LAST CLEAN copy of a file server because they want the data back.
 *
 * WHAT THIS PICKS IS WHICH COPY GETS RESTORED, not a label on it. The choice is passed into
 * CreateRecoverRequests and decides which Recovery_Chain the request is built from, so it cannot
 * be changed after the request exists.
 *
 * AN OBJECT WITH NO CLEAN COPY IS THE CASE THAT MATTERS. Choosing the clean copy when none exists
 * leaves that object out of the recovery entirely — previously in silence. Here the option is
 * disabled and says so, and if it happens anyway the request reports it back.
 */

/** One row per (object, chain element) as the API returns it. */
export interface RedObjectRow {
    objectID: string
    objectSecondary: string
    redId: number | null
    redDateTime: string | null
    greenId: number | null
    greenDateTime: string | null
}

/** 'red' = the compromised copy, 'green' = the last clean one. Absent = follow the global answer. */
export type RedChoice = 'red' | 'green'

interface RedObjectChoicePickerProps {
    redObjects: RedObjectRow[]
    /** The request-wide answer, used for every object with no explicit choice. */
    globalChoice: 'Yes' | 'No'
    choices: Record<string, RedChoice>
    onChange: (next: Record<string, RedChoice>) => void
}

export function RedObjectChoicePicker({
    redObjects,
    globalChoice,
    choices,
    onChange,
}: RedObjectChoicePickerProps) {
    const { t, i18n } = useTranslation()

    // The API returns a row per chain element (Boot, Full, Int, Log) but the choice is per OBJECT:
    // a request is built from one chain or the other, never a mixture. Collapsed here so the
    // customer is asked once per machine rather than once per backup file.
    const objects = useMemo(() => {
        const byId = new Map<string, { objectID: string; hasClean: boolean; redAt: string | null; greenAt: string | null; parts: number }>()
        for (const row of redObjects) {
            const existing = byId.get(row.objectID)
            if (!existing) {
                byId.set(row.objectID, {
                    objectID: row.objectID,
                    hasClean: row.greenId != null,
                    redAt: row.redDateTime,
                    greenAt: row.greenDateTime,
                    parts: 1,
                })
                continue
            }
            // A clean copy anywhere in the chain means the object has a clean chain to fall back to.
            existing.hasClean = existing.hasClean || row.greenId != null
            existing.parts += 1
            if (row.greenDateTime && !existing.greenAt) existing.greenAt = row.greenDateTime
        }
        return [...byId.values()].sort((a, b) => a.objectID.localeCompare(b.objectID))
    }, [redObjects])

    if (objects.length === 0) return null

    const fmt = (iso: string | null) => formatDateTime(iso, i18n.language)
    const globalLabel =
        globalChoice === 'Yes'
            ? t('objects.redChoiceCompromised', 'Compromised copy')
            : t('objects.redChoiceClean', 'Last clean copy')

    const set = (objectID: string, choice: RedChoice | null) => {
        const next = { ...choices }
        if (choice === null) delete next[objectID]
        else next[objectID] = choice
        onChange(next)
    }

    return (
        <div className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
                <Label className="flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                    {t('objects.redChoiceTitle', 'Compromised copies')}
                </Label>
                <span className="text-xs text-muted-foreground">
                    {t('objects.redChoiceCount', {
                        count: objects.length,
                        defaultValue: '{{count}} object(s) affected',
                    })}
                </span>
            </div>

            <FeatheredScrollArea className="rounded-md border" viewportClassName="max-h-56">
                <div className="divide-y">
                    {objects.map((o) => {
                        const choice = choices[o.objectID]
                        return (
                            <div key={o.objectID} className="flex flex-wrap items-center gap-x-4 gap-y-2 p-3">
                                <div className="min-w-[11rem] flex-1">
                                    <div className="text-sm font-medium">{o.objectID}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {t('objects.redChoiceCompromisedAt', 'Compromised')} {fmt(o.redAt)}
                                        {o.hasClean
                                            ? ` · ${t('objects.redChoiceCleanAt', 'Clean')} ${fmt(o.greenAt)}`
                                            : ''}
                                    </div>
                                    {!o.hasClean && (
                                        <Badge variant="outline" className="mt-1 font-normal text-amber-600 dark:text-amber-500">
                                            {t('objects.redChoiceNoClean', 'No clean copy in the vault')}
                                        </Badge>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={choice === undefined ? 'default' : 'outline'}
                                        onClick={() => set(o.objectID, null)}
                                    >
                                        {t('objects.redChoiceDefault', 'Use default')}
                                        <span className="ml-1 opacity-70">({globalLabel})</span>
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={choice === 'red' ? 'default' : 'outline'}
                                        className={choice === 'red' ? 'bg-red-600 text-white hover:bg-red-700' : ''}
                                        onClick={() => set(o.objectID, 'red')}
                                    >
                                        {t('objects.redChoiceCompromised', 'Compromised copy')}
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={choice === 'green' ? 'default' : 'outline'}
                                        className={choice === 'green' ? 'bg-green-600 text-white hover:bg-green-700' : ''}
                                        // Choosing the clean copy when there is none leaves the object out of the
                                        // recovery altogether. Refusing the choice here is far better than accepting
                                        // it and dropping a server.
                                        disabled={!o.hasClean}
                                        title={
                                            o.hasClean
                                                ? undefined
                                                : t(
                                                      'objects.redChoiceNoCleanHelp',
                                                      'There is no clean copy of this object in the vault, so it cannot be recovered from one.',
                                                  )
                                        }
                                        onClick={() => set(o.objectID, 'green')}
                                    >
                                        {t('objects.redChoiceClean', 'Last clean copy')}
                                    </Button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </FeatheredScrollArea>

            <p className="flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {t(
                    'objects.redChoiceHelp',
                    'A compromised copy is recovered as it was found — useful as evidence, but it carries whatever was detected in it. Objects left on the default follow the choice above.',
                )}
            </p>
        </div>
    )
}
